import { collection, addDoc, query, where, orderBy, limit, getDocs, setDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface SubnetPool {
  netuid: number;
  market_cap: string;
  price: string;
  price_change_1_hour: string;
  price_change_1_day: string;
  total_tao: string;
  total_alpha: string;
  alpha_in_pool: string;
  alpha_staked: string;
  timestamp: string;
}

/**
 * Saves subnet pool data to Firestore
 */
export async function saveSubnetPool(data: SubnetPool): Promise<void> {
  try {
    // Only add to subnet-specific subcollection
    const subnetPoolsRef = collection(db, `subnet_pools/${data.netuid}/entries`);
    await addDoc(subnetPoolsRef, {
      ...data,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error saving subnet pool data:', error);
    throw error;
  }
}

/**
 * Gets the latest pool data for a subnet
 */
export async function getLatestSubnetPool(netuid: number): Promise<SubnetPool | null> {
  try {
    const subnetPoolsRef = collection(db, `subnet_pools/${netuid}/entries`);
    const q = query(
      subnetPoolsRef,
      orderBy('timestamp', 'desc'),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    return snapshot.docs[0].data() as SubnetPool;
  } catch (error) {
    console.error('Error getting latest subnet pool data:', error);
    throw error;
  }
}

/**
 * Gets historical pool data for a subnet
 */
export async function getSubnetPoolHistory(
  netuid: number,
  limit: number = 30
): Promise<SubnetPool[]> {
  try {
    const subnetPoolsRef = collection(db, `subnet_pools/${netuid}/entries`);
    const q = query(
      subnetPoolsRef,
      orderBy('timestamp', 'desc'),
      limit(limit)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as SubnetPool);
  } catch (error) {
    console.error('Error getting subnet pool history:', error);
    throw error;
  }
}

/**
 * Updates the subnet overview data in Firestore
 */
export async function updateSubnetOverview(netuid: number, data: any): Promise<void> {
  try {
    // Create new pool entry
    const poolData: SubnetPool = {
      netuid,
      market_cap: data.market_cap || '0',
      price: data.price || '0',
      price_change_1_hour: data.price_change_1_hour || '0',
      price_change_1_day: data.price_change_1_day || '0',
      total_tao: data.total_tao || '0',
      total_alpha: data.total_alpha || '0',
      alpha_in_pool: data.alpha_in_pool || '0',
      alpha_staked: data.alpha_staked || '0',
      timestamp: new Date().toISOString()
    };

    await saveSubnetPool(poolData);

    // Store volume data directly in the subnet pool document
    // This replaces the previous updateVolumeFromOverview call
    const volumeData = {
      volume_24h: Number(data.tao_volume_24_hr || '0'),
      buy_volume_24h: Number(data.tao_buy_volume_24_hr || '0'),
      sell_volume_24h: Number(data.tao_sell_volume_24_hr || '0'),
      buys_24h: data.buys_24_hr || 0,
      sells_24h: data.sells_24_hr || 0,
      timestamp: new Date().toISOString()
    };

    // Store volume data in a separate document in the subnet_volume collection
    try {
      const volumeRef = collection(db, 'subnet_volume', netuid.toString(), 'entries');
      await addDoc(volumeRef, volumeData);
      console.log(`Saved volume data for subnet ${netuid}`);
    } catch (volumeError) {
      console.error('Error saving volume data:', volumeError);
      // Continue execution even if volume data saving fails
    }
  } catch (error) {
    console.error('Error updating subnet overview:', error);
    throw error;
  }
}